import {
  CORONA_TESTED,
  OVERHEIGHT,
  PREDICAMENT_STATE_YES,
  REHABILITATION_PAYMENT_OPTION,
  SEARCH_TYPE_REHABILITATION,
  VALIDATION_MISSING,
  YES_NO_FIELD_OPTIONS,
} from "core/consts";
import {
  WEIGHT_RANGES,
  formatPickUpTime,
  getAges,
  getInitialInsuranceStatus,
  hasMobileSolution,
} from "core/model/patients";
import {
  convertBirthDateIn,
  convertBirthDateOut,
  getAgeFromDate,
  validateBirthDate,
} from "core/model/utils/dates";
import { validateZipcode } from "core/model/utils/location";
import {
  EncryptedField,
  Financing,
  MedicalProductType,
  PatientWhitelist,
  SearchType,
} from "core/types";
import { format } from "date-fns";
import { requiredHouseNumber } from "dsl/molecules/AddressInputGroup/utils";
import { Iterable } from "immutable";
import {
  ValidationFunction,
  algoliaReactSelectDef,
  convertEncryptedFieldIn,
  convertEncryptedFieldOut,
  convertOutAssertPath,
  encryptedDef,
  required,
  validateHeight,
  validateWeight,
  valueDef,
  valueDefActivableInput,
  whitelistActivableInputModelDefinition,
  whitelistEncryptedDef,
  whitelistGroupDef,
  whitelistValueDef,
} from "react-forms-state";
import { Whitelist } from "react-forms-state/src/types";
import { AssessmentStepTypeName } from "./AssessmentStep";

export const requiredBySearchType = (
  requiredInTypes: number[],
  searchType?: SearchType | null | undefined,
) => {
  return requiredInTypes.includes(searchType || -1);
};

export const covidValidation: ValidationFunction = (
  value: any,
  { translations },
) => {
  if (value.corona_state === CORONA_TESTED && !value.corona_test_results) {
    return {
      corona_test_results: translations.general.required,
    };
  }
  return true;
};

export const coronaWhitelist = ({
  whitelist,
}: {
  whitelist: PatientWhitelist[AssessmentStepTypeName] | undefined;
}) => {
  return {
    ...whitelistValueDef("corona_state", {
      whitelist,
    }),
    corona: {
      ...whitelistValueDef("test_result", {
        out_name: "corona_test_results",
        whitelist,
      }),
      ...whitelistValueDef("description", {
        out_name: "corona_description",
        whitelist,
      }),
      ...whitelistValueDef("vaccinated", {
        out_name: "vaccinated",
        validate(value) {
          // make text field required, when checkbox is selected
          if (typeof value === "string") {
            return value.length > 0;
          }

          return true;
        },
        whitelist,
      }),
    },
  };
};

export const productsWhitelistDef = ({
  whitelist,
}: {
  whitelist: PatientWhitelist[AssessmentStepTypeName] | undefined;
}) => {
  return whitelistValueDef("products", {
    convertOut: (
      values: any | null | undefined,
    ): MedicalProductType[] | null => {
      const products = (
        Iterable.isIterable(values) ? values?.toJS() : values
      ) as MedicalProductType[] | null;

      const definedProducts = products?.truthy();

      if (!definedProducts?.length) return null;

      return definedProducts.map(
        ({
          description,
          id,
          location,
          name,
          product_group,
          subgroup,
          type,
        }: MedicalProductType) => ({
          description,
          id,
          location,
          name,
          product_group,
          subgroup,
          type,
        }),
      );
    },
    validate: (values: any) => {
      const products = (
        Iterable.isIterable(values) ? values?.toJS() : values
      ) as MedicalProductType[] | null;

      return !!products?.truthy()?.length;
    },
    whitelist,
  });
};

type PayersInsuranceObj = {
  label?: string | null;
  value?: number | string | null;
};

type PayersInfoValue =
  | Iterable<"label" | "value", number | string | null>
  | PayersInsuranceObj
  | string
  | null;

const getPayersInsurance = (
  value: PayersInfoValue,
): PayersInsuranceObj | null => {
  if (value == null || typeof value !== "object") return null;

  const parsedValue =
    "toJS" in value ? (value.toJS() as PayersInsuranceObj) : value;
  return parsedValue;
};

export const validatePayersInsurance = (
  value: PayersInfoValue,
  props: any,
  globalValue?: Iterable<any, any> | null,
) => {
  const payersInsurance = getPayersInsurance(value);
  if (
    globalValue?.get("selected_payment_method") ===
    REHABILITATION_PAYMENT_OPTION.INSURANCE
  ) {
    if (payersInsurance && (!payersInsurance.label || !payersInsurance.value))
      return false;
  }

  return true;
};

export const convertOutPayersInfoAssessment = (
  value: PayersInfoValue,
  globalValue: Iterable<any, any> | null,
  assertedPaymentOption: number,
) => {
  if (!globalValue) {
    return null;
  }

  const selectedPaymentOption = globalValue.get("selected_payment_method");
  if (selectedPaymentOption !== assertedPaymentOption) {
    return null;
  }

  return convertOutPayers(value);
};

export const convertOutPayers = (value: PayersInfoValue) => {
  const payersInsurance = getPayersInsurance(value);
  if (!payersInsurance) return value;

  const payersInsuranceId = payersInsurance?.value
    ? parseInt(payersInsurance.value as string)
    : 0;

  if (payersInsuranceId <= 0 || isNaN(payersInsuranceId)) {
    return null;
  }

  const result = {
    id: payersInsuranceId,
    name: payersInsurance?.label || "",
  };

  return result;
};

const removeLocation = (props: any) => {
  const initialSolutions = props?.initialAuction?.solutions;
  const currentSolutions = props?.formInputValue?.solutions;

  let initialAuctionHasMobile, currentPatientHasMobile;

  if (initialSolutions && currentSolutions) {
    initialAuctionHasMobile = hasMobileSolution(initialSolutions);
    currentPatientHasMobile = hasMobileSolution(currentSolutions);
  }

  return initialAuctionHasMobile !== currentPatientHasMobile;
};

const defaultLocationValue = (location: Location | undefined, props: any) => {
  const locationRemoved = removeLocation(props);

  if (!location || locationRemoved) {
    return {};
  }
  return null;
};

export function validateAddress(
  address:
    | {
        city: string;
        coordinates: { latitude: number; longitude: number; zipcode: string };
        country?: string;
        encrypted_house_number?: EncryptedField;
        floor?: number;
        house_number?: string;
        radius: number;
        street: string;
        zipcode: string;
      }
    | undefined,
): boolean {
  if (
    !address?.street ||
    !address.zipcode ||
    !address.city ||
    !(address.house_number || address.encrypted_house_number)
  ) {
    return false;
  }
  return true;
}

export const locationDef = (
  elementName: string,
  options: {
    fieldRequired?: boolean;
    validate?: ValidationFunction;
    whitelist: PatientWhitelist[AssessmentStepTypeName] | undefined;
  },
) => ({
  ...whitelistValueDef(elementName, {
    convertIn: (location, props) => {
      const defaultLocation = defaultLocationValue(location, props);
      if (defaultLocation) return defaultLocation;

      const houseNumberMap = location.getIn(["encrypted_house_number"], null);
      const houseNumberEncrypted = convertEncryptedFieldIn(houseNumberMap);

      return {
        city: location.getIn(["city"], null),
        coordinates: {
          latitude: location.getIn(["latitude"], null),
          longitude: location.getIn(["longitude"], null),
        },
        encrypted_house_number: { decrypted: houseNumberEncrypted },
        floor: location.getIn(["floor"], null),
        house_number: location.getIn(["house_number"], null),
        radius: location.getIn(["radius_in_meter"], null),
        street: location.getIn(["street"], null),
        zipcode: location.getIn(["zipcode"], null),
      };
    },
    convertOut: (location) => {
      if (!location) return {};
      return {
        city: location.getIn(["city"], null),
        encrypted_house_number: convertEncryptedFieldOut(
          location.getIn(["encrypted_house_number", "decrypted"], null),
        ),
        floor: location.getIn(["floor"], null),
        latitude: location.getIn(["coordinates", "latitude"], null),
        longitude: location.getIn(["coordinates", "longitude"], null),
        radius_in_meter: location.getIn(["radius"], null),
        street: location.getIn(["street"], null),
        zipcode: location.getIn(["zipcode"], null),
      };
    },
    validate: (value, props, globalValue) => {
      if (options?.validate) {
        return options.validate(value, props, globalValue);
      }

      if (!options.fieldRequired) {
        return true;
      }

      const zipcodeValid = validateZipcode(options.fieldRequired)(value);
      if (zipcodeValid !== true) {
        return zipcodeValid;
      }

      const { search_type, share_patient_data_automatically, solutions } =
        props?.formInputValue?.auction ?? {};
      if (
        requiredHouseNumber({
          search_type,
          share_patient_data_automatically,
          solutions,
        }) &&
        !value.getIn(["radius"], 0)
      ) {
        return VALIDATION_MISSING;
      }

      return true;
    },
    whitelist: options.whitelist,
  }),
});

export const carelevelDef = ({
  whitelist,
}: {
  whitelist: PatientWhitelist[AssessmentStepTypeName] | undefined;
}) =>
  whitelistGroupDef("carelevel", {
    convertIn: (value: Record<string, any> | null | undefined) =>
      !value
        ? {
            expedited_request: null,
            has_applied: null,
            higher_level: null,
            level: null,
          }
        : value,
    out: "carelevel",
    whitelist,
    ...whitelistValueDef("level", {
      fieldRequired: true,
      whitelist,
    }),
    ...whitelistValueDef("has_applied", { whitelist }),
    ...whitelistValueDef("higher_level", { whitelist }),
    ...whitelistValueDef("expedited_request", { whitelist }),
  });

export const entitlementGroupDef = ({
  whitelist,
}: {
  whitelist: PatientWhitelist[AssessmentStepTypeName] | undefined;
}) =>
  whitelistValueDef("benefit_entitlement_group", {
    convertIn: (value: Record<string, any> | null | undefined) => {
      if (!value) {
        return null;
      }
      if (typeof value === "string" && value === "") {
        return "";
      }
      const plainValue = Iterable.isIterable(value)
        ? (value.toJS() as Record<string, any>)
        : value;
      if (
        typeof plainValue === "object" &&
        !Array.isArray(plainValue) &&
        ("available" in plainValue || "description" in plainValue)
      ) {
        const { available, description } = plainValue;
        if (available === true) {
          return typeof description === "string" && description.trim() !== ""
            ? description
            : "";
        }
        if (available === false) {
          return YES_NO_FIELD_OPTIONS.NO;
        }
        return null;
      }
      return value;
    },
    convertOut: (value: any) => {
      if (value === null || value === undefined) {
        return undefined;
      }

      if (typeof value === "string") {
        return {
          available: true,
          description: value === "" ? null : value,
        };
      }

      if (value === YES_NO_FIELD_OPTIONS.YES) {
        return {
          available: true,
          description: null,
        };
      }

      if (value === YES_NO_FIELD_OPTIONS.NO) {
        return {
          available: false,
          description: null,
        };
      }

      if (typeof value === "object" && "available" in value) {
        if (value.available === null || value.available === undefined) {
          return undefined;
        }
        return {
          available: value.available,
          description: value.description ?? null,
        };
      }

      return undefined;
    },
    whitelist,
  });

export const payersDef = ({
  whitelist,
}: {
  whitelist: PatientWhitelist[AssessmentStepTypeName] | undefined;
}) => ({
  payers: {
    ...whitelistValueDef("payers", {
      whitelist,
    }),
    ...whitelistValueDef("selected_payment_method", {
      validate: (value, props) =>
        required({ errorString: props.translations.actions.missing })(
          value,
          props,
        ),
      whitelist,
    }),
    ...algoliaReactSelectDef("insurance", {
      convertOut: (value, _props, globalValue) =>
        convertOutPayersInfoAssessment(
          value,
          globalValue,
          REHABILITATION_PAYMENT_OPTION.INSURANCE,
        ),
      out_name: "payers_insurance",
      validate: validatePayersInsurance,
      whitelist,
    }),
    ...whitelistValueDef("civil_servants_aid", {
      convertOut: (value, _props, globalValue) =>
        convertOutPayersInfoAssessment(
          value,
          globalValue,
          REHABILITATION_PAYMENT_OPTION.CIVIL_SERVANTS_AID,
        ),
      whitelist,
    }),
    ...whitelistValueDef("public_accident_insurance", {
      convertOut: (value, _props, globalValue) =>
        convertOutPayersInfoAssessment(
          value,
          globalValue,
          REHABILITATION_PAYMENT_OPTION.PUBLIC_ACCIDENT_INSURANCE,
        ),
      whitelist,
    }),
    ...algoliaReactSelectDef("public_pension", {
      convertOut: (value, _props, globalValue) =>
        convertOutPayersInfoAssessment(
          value,
          globalValue,
          REHABILITATION_PAYMENT_OPTION.PUBLIC_PENSION,
        ),
      validate: (value, props, globalValue) => {
        const selected = globalValue?.get("selected_payment_method");
        const isPublicPension =
          selected === REHABILITATION_PAYMENT_OPTION.PUBLIC_PENSION;

        if (!value && isPublicPension) {
          return {
            customMessage:
              props.translations.assessments.formValidation
                .publicPensionMissing,
          };
        }

        return true;
      },
      whitelist,
    }),
    ...whitelistValueDef("social_service_department", {
      convertOut: (value, _props, globalValue) =>
        convertOutPayersInfoAssessment(
          value,
          globalValue,
          REHABILITATION_PAYMENT_OPTION.SOCIAL_SERVICE_DEPARTMENT,
        ),
      whitelist,
    }),
    ...whitelistValueDef("self_payer", {
      convertOut: (value, _props, globalValue) =>
        convertOutPayersInfoAssessment(
          value,
          globalValue,
          REHABILITATION_PAYMENT_OPTION.SELF_PAYER,
        ),
      whitelist,
    }),
    ...whitelistValueDef("other_payer", {
      convertOut: (value, _props, globalValue) =>
        convertOutPayersInfoAssessment(
          value,
          globalValue,
          REHABILITATION_PAYMENT_OPTION.OTHER,
        ),
      whitelist,
    }),
    ...whitelistValueDef("further_information", { whitelist }),
    ...whitelistValueDef("patient_has_health_insurance_state", {
      whitelist,
    }),
    ...whitelistValueDef("supplementary_insurance", {
      validate(value, props, globalValue) {
        const state = globalValue?.get("supplementary_insurance_state");

        if (
          typeof value === "string" &&
          !value &&
          state === PREDICAMENT_STATE_YES
        ) {
          return {
            customMessage:
              props.translations.assessments.formValidation
                .supplementaryInsuranceDetailsMissing,
          };
        }

        return true;
      },
      whitelist,
    }),
    ...whitelistValueDef("supplementary_insurance_state", { whitelist }),
    ...whitelistValueDef("supplementary_payment", { whitelist }),
    ...whitelistValueDef("supplementary_payment_state", { whitelist }),
    ...whitelistValueDef("free_from_supplementary_payment_state", {
      whitelist,
    }),
  },
});

const getGuardian = (props: any, index: number) => {
  const guardians =
    props.formInputValue?.patient?.profile?.communication?.guardians;

  if (Array.isArray(guardians) && guardians?.length > index) {
    return guardians[index];
  }
  return null;
};

const getRelative = (props: any, index: number) => {
  const relatives =
    props.formInputValue?.patient?.profile?.communication?.relatives;
  if (Array.isArray(relatives) && relatives?.length > index) {
    return relatives[index];
  }
  return null;
};

const getGuardianConvertIn =
  (propertyKey: string, guardianIndex: number, isEncrypted = true) =>
  (_input: any, props: any) => {
    const guardian = getGuardian(props, guardianIndex);
    return isEncrypted
      ? guardian?.[propertyKey]?.decrypted || null
      : guardian?.[propertyKey] || null;
  };

const GUARDIAN_FIELDS = [
  { fieldName: "guardian_first_name", propertyKey: "first_name" },
  { fieldName: "guardian_last_name", propertyKey: "last_name" },
  {
    fieldName: "guardian_phone",
    propertyKey: "phone",
    // #TODO commenting for https://recare.atlassian.net/browse/TC-2266
    // validate: validatePhoneNumber(false),
  },
  { fieldName: "guardian_responsibilities", propertyKey: "responsibilities" },
  {
    fieldName: "guardian_house_number",
    propertyKey: "house_number",
    // #TODO commenting for https://recare.atlassian.net/browse/TC-2266
    // validate: validateHouseNumber,
  },
  { fieldName: "guardian_street", propertyKey: "street" },
  {
    fieldName: "guardian_zipcode",
    propertyKey: "zipcode",
    // #TODO commenting for https://recare.atlassian.net/browse/TC-2266
    // validate: validateZipcode(false),
  },
  { fieldName: "guardian_city", propertyKey: "city" },
  { fieldName: "guardian_type", isEncrypted: false, propertyKey: "type" },
];

const getRelativeConvertIn =
  (propertyKey: string, relativeIndex: number) => (_input: any, props: any) => {
    const relative = getRelative(props, relativeIndex);
    return relative?.[propertyKey]?.decrypted || null;
  };

const RELATIVE_FIELDS = [
  { fieldName: "relative_first_name", propertyKey: "first_name" },
  { fieldName: "relative_last_name", propertyKey: "last_name" },
  {
    fieldName: "relative_phone",
    propertyKey: "phone",
    // #TODO commenting for https://recare.atlassian.net/browse/TC-2266
    // validate: validatePhoneNumber(false),
  },
  {
    fieldName: "relative_house_number",
    propertyKey: "house_number",
    // #TODO commenting for https://recare.atlassian.net/browse/TC-2266
    // validate: validateHouseNumber,
  },
  { fieldName: "relative_street", propertyKey: "street" },
  {
    fieldName: "relative_zipcode",
    propertyKey: "zipcode",
    // #TODO commenting for https://recare.atlassian.net/browse/TC-2266
    // validate: validateZipcode(false),
  },
  { fieldName: "relative_city", propertyKey: "city" },
  { fieldName: "relative_relationship", propertyKey: "relationship" },
  {
    fieldName: "relative_relationship_other",
    propertyKey: "relationship_other",
  },
];

export const createRelativesModelDefinition = () => {
  const fieldDefinitions = RELATIVE_FIELDS.reduce(
    (acc, { fieldName, propertyKey }) => ({
      ...acc,
      ...encryptedDef(`${fieldName}_1`, {
        convertIn: getRelativeConvertIn(propertyKey, 0),
      }),
      ...encryptedDef(`${fieldName}_2`, {
        convertIn: getRelativeConvertIn(propertyKey, 1),
      }),
    }),
    {},
  );

  return {
    ...fieldDefinitions,
    convertOut: (_data: any, _props?: any, globalValue?: any) => {
      const hasRelativeData = (suffix: string) => {
        return RELATIVE_FIELDS.some(({ fieldName }) => {
          const value = globalValue.getIn([`${fieldName}_${suffix}`]);
          return value && value !== "";
        });
      };

      const createRelativeObject = (suffix: string) => {
        return {
          city: convertEncryptedFieldOut(
            globalValue.getIn([`relative_city_${suffix}`]),
          ),
          first_name: convertEncryptedFieldOut(
            globalValue.getIn([`relative_first_name_${suffix}`]),
          ),
          house_number: convertEncryptedFieldOut(
            globalValue.getIn([`relative_house_number_${suffix}`]),
          ),
          last_name: convertEncryptedFieldOut(
            globalValue.getIn([`relative_last_name_${suffix}`]),
          ),
          phone: convertEncryptedFieldOut(
            globalValue.getIn([`relative_phone_${suffix}`]),
          ),
          relationship: convertEncryptedFieldOut(
            globalValue.getIn([`relative_relationship_${suffix}`]),
          ),
          relationship_other: convertEncryptedFieldOut(
            globalValue.getIn([`relative_relationship_other_${suffix}`]),
          ),
          street: convertEncryptedFieldOut(
            globalValue.getIn([`relative_street_${suffix}`]),
          ),
          zipcode: convertEncryptedFieldOut(
            globalValue.getIn([`relative_zipcode_${suffix}`]),
          ),
        };
      };

      const relatives = [];

      if (hasRelativeData("1")) {
        relatives.push(createRelativeObject("1"));
      }

      if (hasRelativeData("2")) {
        relatives.push(createRelativeObject("2"));
      }

      return relatives.length > 0 ? relatives : null;
    },
  };
};

export const createGuardiansModelDefinition = () => {
  const fieldDefinitions = GUARDIAN_FIELDS.reduce(
    (acc, { fieldName, isEncrypted, propertyKey }) => ({
      ...acc,
      ...encryptedDef(`${fieldName}_1`, {
        convertIn: getGuardianConvertIn(propertyKey, 0, isEncrypted !== false),
      }),
      ...encryptedDef(`${fieldName}_2`, {
        convertIn: getGuardianConvertIn(propertyKey, 1, isEncrypted !== false),
      }),
    }),
    {},
  );

  return {
    ...fieldDefinitions,
    convertOut: (_data: any, _props?: any, globalValue?: any) => {
      const hasGuardianData = (suffix: string) => {
        return GUARDIAN_FIELDS.some(({ fieldName }) => {
          const value = globalValue.getIn([`${fieldName}_${suffix}`]);
          return value && value !== "";
        });
      };

      const createGuardianObject = (suffix: string) => ({
        city: convertEncryptedFieldOut(
          globalValue.getIn([`guardian_city_${suffix}`]),
        ),
        first_name: convertEncryptedFieldOut(
          globalValue.getIn([`guardian_first_name_${suffix}`]),
        ),
        house_number: convertEncryptedFieldOut(
          globalValue.getIn([`guardian_house_number_${suffix}`]),
        ),
        last_name: convertEncryptedFieldOut(
          globalValue.getIn([`guardian_last_name_${suffix}`]),
        ),
        phone: convertEncryptedFieldOut(
          globalValue.getIn([`guardian_phone_${suffix}`]),
        ),
        responsibilities: convertEncryptedFieldOut(
          globalValue.getIn([`guardian_responsibilities_${suffix}`]),
        ),
        street: convertEncryptedFieldOut(
          globalValue.getIn([`guardian_street_${suffix}`]),
        ),
        type: globalValue.getIn([`guardian_type_${suffix}`]),
        zipcode: convertEncryptedFieldOut(
          globalValue.getIn([`guardian_zipcode_${suffix}`]),
        ),
      });

      const guardians = [];

      if (hasGuardianData("1")) {
        guardians.push(createGuardianObject("1"));
      }

      if (hasGuardianData("2")) {
        guardians.push(createGuardianObject("2"));
      }

      return guardians.length > 0 ? guardians : null;
    },
  };
};

export const communicationDef = ({
  whitelist,
}: {
  whitelist: PatientWhitelist[AssessmentStepTypeName] | undefined;
}) => {
  return {
    ...whitelistValueDef("decisions_and_language", {
      whitelist,
    }),
    ...whitelistValueDef("patient_cannot_communicate_in_german", {
      whitelist,
    }),
    ...whitelistValueDef("patient_language", {
      convertIn: (input: string | null) => input || "",
      convertOut: convertOutAssertPath(
        "patient_cannot_communicate_in_german",
        "",
      ),
      whitelist,
    }),
    ...whitelistValueDef("has_guardian", { whitelist }),
    ...whitelistEncryptedDef("guardian_contact_information", {
      whitelist,
    }),
    ...whitelistValueDef("guardian_requested", { whitelist }),
    ...whitelistValueDef("relatives_available", { whitelist }),
    ...whitelistEncryptedDef("relatives_description", {
      whitelist,
    }),

    ...whitelistValueDef("patient_is_contact", { whitelist }),
    ...whitelistEncryptedDef("patient_is_contact_description", {
      whitelist,
    }),
    guardians: createGuardiansModelDefinition(),
    relatives: createRelativesModelDefinition(),
  };
};

export const ageDef = (
  whitelist: Record<string, boolean> | undefined,
): Record<string, any> => {
  const age_interval = whitelistValueDef("age_interval", {
    convertIn: (input: any) => {
      const age_interval = Iterable.isIterable(input) ? input.toJS() : input;
      if (age_interval) {
        if (!age_interval.min) {
          return `< ${age_interval.max}`;
        }
        if (!age_interval.max) {
          return `> ${age_interval.min}`;
        }
        return `${age_interval.min} - ${age_interval.max - 1}`;
      }

      return null;
    },
    convertOut: (output: any, props: any, formOutValue: any) => {
      const age = getAgeFromDate(formOutValue.getIn(["birth_date"]));
      const ageRanges = getAges(props.formInputValue.search_type);

      const range = ageRanges.find(({ max, min }) => {
        if (min == null) return age <= (max || 9999);
        if (max == null) return age >= (min || 0);
        return age >= min && age <= max;
      });
      return range;
    },
    whitelist,
  });

  return {
    ...age_interval,
    ...whitelistEncryptedDef("birth_date", {
      convertIn: convertBirthDateIn,
      convertOut: convertBirthDateOut,
      fieldRequired: true,
      validate: validateBirthDate,
      whitelist,
    }),
  };
};

export const INFECTION_GROUP_FIELDS = [
  "mrsa",
  "vre",
  "three_mrgn",
  "four_mrgn",
  "clostridien",
  "hepatitis_c",
  "other",
  "requires_isolation",
] as const;

export const INFECTIONS_SELECTION_ELEMENT_NAME = "infections_selection";

export const infectionsAndGermsDef = ({
  whitelist,
}: {
  whitelist: PatientWhitelist[AssessmentStepTypeName] | undefined;
}) => ({
  ...whitelistValueDef("infection_and_germs_state", {
    defaultValue: null,
    validate: (value: number | null, { translations }) => {
      return required()(value) === true && value !== 0
        ? true
        : {
            customMessage:
              translations.patient.medicalDiagnosis.currentInfectionsError,
          };
    },
    whitelist,
  }),
  ...valueDef(INFECTIONS_SELECTION_ELEMENT_NAME, {
    convertOut: () => undefined,
    defaultValue: null,
    validate: (_value, { translations }, globalValue) => {
      const state = globalValue?.get?.("infection_and_germs_state");
      if (state !== PREDICAMENT_STATE_YES) return true;
      const hasAtLeastOneInfection = INFECTION_GROUP_FIELDS.some((key) => {
        const nestedValue = globalValue?.getIn?.(["infection_and_germs", key]);
        const flatValue = globalValue?.get?.(key);
        const fieldValue = nestedValue != null ? nestedValue : flatValue;
        return fieldValue != null && fieldValue !== false;
      });
      return hasAtLeastOneInfection
        ? true
        : {
            customMessage:
              translations.patient.medicalDiagnosis.currentInfectionsError,
          };
    },
  }),
  ...valueDefActivableInput("diagnosis.infection_and_germs"),
  ...whitelistGroupDef("infection_and_germs", {
    convertOut: (value: any, _: Record<string, any>, allValues: any) => {
      if (allValues?.getIn !== undefined) {
        const stateValue = allValues.getIn(["infection_and_germs_state"]);
        const remarksValue = allValues.getIn(["remarks"]);
        if (stateValue !== PREDICAMENT_STATE_YES) {
          return remarksValue
            ? {
                clostridien: null,
                four_mrgn: null,
                hepatitis_c: null,
                mrsa: null,
                other: null,
                remarks: remarksValue,
                requires_isolation: null,
                three_mrgn: null,
                vre: null,
              }
            : null;
        }
      }
      return value;
    },
    whitelist,
    ...whitelistValueDef("mrsa", { whitelist }),
    ...whitelistValueDef("clostridien", { whitelist }),
    ...whitelistValueDef("three_mrgn", { whitelist }),
    ...whitelistValueDef("four_mrgn", { whitelist }),
    ...whitelistValueDef("hepatitis_c", { whitelist }),
    ...whitelistValueDef("vre", { whitelist }),
    ...whitelistValueDef("other", {
      validate(value, props) {
        if (typeof value === "string" && !value) {
          return required({ errorString: props.translations.actions.missing })(
            value,
            props,
          );
        }

        return true;
      },
      whitelist,
    }),
    ...whitelistValueDef("remarks", { whitelist }),
    ...whitelistValueDef("requires_isolation", { whitelist }),
  }),
});

export const getWeightModel = ({
  whitelist,
}: {
  whitelist: PatientWhitelist[AssessmentStepTypeName] | undefined;
}) => {
  const weight_interval = whitelistValueDef("weight_interval", {
    convertOut: (output: any, props: any, formOutValue: any) => {
      const weight = formOutValue.getIn(["weight"]);
      if (!weight) return null;

      const range = WEIGHT_RANGES.find(
        (queriedRange) =>
          queriedRange?.min <= Number(weight) &&
          queriedRange?.max >= Number(weight),
      );
      return range;
    },
    whitelist,
  });

  return {
    ...weight_interval,
    ...whitelistEncryptedDef("weight", {
      validate: validateWeight,
      whitelist,
    }),
  };
};

export const getHeightModel = ({
  whitelist,
}: {
  whitelist: PatientWhitelist[AssessmentStepTypeName] | undefined;
}) => {
  const overHeight = whitelistValueDef("height_interval", {
    convertOut: (output: any, props: any, formOutValue: any) => {
      const height = formOutValue.getIn(["height"]);

      if (height && Number(height) > OVERHEIGHT) {
        return { max: null, min: OVERHEIGHT };
      }
      if (height) {
        return { max: null, min: 0 };
      }
      return null;
    },
    whitelist,
  });

  return {
    ...overHeight,
    ...whitelistEncryptedDef("height", {
      validate: validateHeight,
      whitelist,
    }),
  };
};

export const financingSectionDef = ({
  searchType,
  whitelist,
}: {
  searchType?: SearchType | null;
  whitelist: PatientWhitelist[AssessmentStepTypeName] | undefined;
}) => ({
  financing: {
    ...whitelistValueDef("financing", {
      whitelist,
    }),
    ...carelevelDef({ whitelist }),
    ...entitlementGroupDef({ whitelist }),
    ...whitelistValueDef("public_care_insurance_status", {
      whitelist,
    }),
    ...whitelistValueDef("patient_has_health_insurance_state", {
      whitelist,
    }),
    ...whitelistValueDef("reimbursment_for_help_at_home", {
      whitelist,
    }),
    ...whitelistValueDef("reimbursment_secured", {
      whitelist,
    }),
    ...whitelistValueDef("preventative_care", {
      whitelist,
    }),
    ...whitelistValueDef("short_term_care", {
      whitelist,
    }),
    ...whitelistValueDef("relief_services", { whitelist }),
    ...whitelistValueDef("private_payment_bool", { whitelist }),
    ...whitelistValueDef("social_help_recipient_bool", {
      whitelist,
    }),
    ...whitelistValueDef("more_information", { whitelist }),
    ...algoliaReactSelectDef("insurance", {
      convertOut: () => undefined, // BE doesn't handle this field
      out_name: "financing_insurance",
      whitelist,
    }),
    ...whitelistEncryptedDef("insurance_number", {
      fieldRequired: searchType === SEARCH_TYPE_REHABILITATION,
      whitelist,
    }),
    ...whitelistValueDef("insurance_additional_information", {
      whitelist,
    }),
    ...whitelistValueDef("insurance_status", {
      convertIn: (
        value: number | null | undefined,
        _props: Record<string, any>,
        global: Record<string, any>,
      ) => {
        const auction = (
          Iterable.isIterable(global) ? global.toJS() : global
        ) as
          | { patient?: { profile?: { financing?: Financing } } }
          | null
          | undefined;
        const financing = auction?.patient?.profile?.financing;
        return getInitialInsuranceStatus({
          ...(financing ?? {}),
          insurance_status: value ?? null,
        });
      },
      whitelist,
    }),
    ...whitelistValueDef("insurance_id", { whitelist }),
    ...whitelistValueDef("payout", {
      whitelist,
    }),
  },
});

const timeConvertOut = (value: Date | number) => {
  if (!value) return { hour: null, minute: null };
  const stringValue = format(value, "HH:mm");
  const [hour, minute] = stringValue.split(":");
  return {
    hour: Number(hour),
    minute: Number(minute),
  };
};

export const pickUpTimeDef = ({
  whitelist,
}: {
  whitelist: PatientWhitelist[AssessmentStepTypeName] | undefined;
}) =>
  whitelistGroupDef("pick_up", {
    whitelist,
    ...valueDef("start", {
      convertIn: formatPickUpTime,
      convertOut: timeConvertOut,
      fieldRequired: true,
    }),
    ...valueDef("end", {
      convertIn: formatPickUpTime,
      convertOut: timeConvertOut,
      fieldRequired: true,
    }),
  });

export const barrierFreeWhitelist = ({
  whitelist,
}: {
  whitelist: PatientWhitelist[AssessmentStepTypeName] | undefined;
}) => ({
  ...whitelistValueDef("barrier_free", { whitelist }),
  ...whitelistValueDef("barrier_free_description", { whitelist }),
});

export const unsafeDomesticSituationWhitelist = ({
  whitelist,
}: {
  whitelist: PatientWhitelist[AssessmentStepTypeName] | undefined;
}) => ({
  ...whitelistValueDef("unsafe_current_domestic_situation_state", {
    whitelist,
  }),
  ...whitelistValueDef("unsafe_current_domestic_situation", { whitelist }),
});

export const transportMonitoringNeedsDef = ({
  whitelist,
}: {
  whitelist: PatientWhitelist[AssessmentStepTypeName] | undefined;
}) =>
  whitelistGroupDef("monitoring_needs", {
    whitelist,
    ...whitelistValueDef("ecg", {
      out_name: "monitoring_ecg",
      whitelist,
    }),
    ...whitelistValueDef("pulse_oximeter", {
      out_name: "monitoring_pulse",
      whitelist,
    }),
  });

export const accessAndDrainageModelDefinition = ({
  whitelist,
}: {
  whitelist: Whitelist;
}) =>
  whitelistActivableInputModelDefinition(
    "patient.care_needs.access_drainages",
    "access_drainages",
    {
      whitelist,
      ...whitelistValueDef("tracheal_cannula", { whitelist }),
      ...whitelistValueDef("gastric_tube", { whitelist }),
      ...whitelistValueDef("central_venous_catheter", { whitelist }),
      ...whitelistValueDef("peripheral_venous_catheter", { whitelist }),
      ...whitelistValueDef("urinary_catheter", { whitelist }),
      ...whitelistValueDef("suprapubic_catheter", { whitelist }),
      ...whitelistValueDef("permanent_catheter", { whitelist }),
      ...whitelistValueDef("drainage", { whitelist }),
      ...whitelistValueDef("intravenous_injection", { whitelist }),
      ...whitelistValueDef("other_access_drainages", {
        validate(value, props) {
          if (typeof value === "string" && !value) {
            return required({
              errorString: props.translations.actions.missing,
            })(value, props);
          }

          return true;
        },
        whitelist,
      }),
    },
  );

export const monitoringModelDefinition = ({
  whitelist,
}: {
  whitelist: Whitelist;
}) =>
  whitelistActivableInputModelDefinition(
    "patient.care_needs.monitoring",
    "monitoring",
    {
      whitelist,
      ...whitelistValueDef("circulation", { whitelist }),
      ...whitelistValueDef("respiration", { whitelist }),
      ...whitelistValueDef("metabolism", { whitelist }),
    },
  );

export const medicationModelDefinition = ({
  whitelist,
}: {
  whitelist: Whitelist;
}) =>
  whitelistActivableInputModelDefinition(
    "patient.care_needs.medication",
    "medication",
    {
      whitelist,
      ...whitelistValueDef("description", {
        out_name: "medication_description",
        whitelist,
      }),
      ...whitelistValueDef("blood_thinner", {
        whitelist,
      }),
    },
  );

export const woundsModelDefinition = ({
  whitelist,
}: {
  whitelist: Whitelist;
}) =>
  whitelistActivableInputModelDefinition(
    "patient.care_needs.wound_care",
    "wound_care",
    {
      whitelist,
      ...whitelistValueDef("description", {
        out_name: "wound_care_description",
        whitelist,
      }),
    },
  );

export const breathingModelDefinition = ({
  whitelist,
}: {
  whitelist: Whitelist;
}) =>
  whitelistActivableInputModelDefinition(
    "patient.care_needs.breathing",
    "breathing",
    {
      whitelist,
      ...whitelistValueDef("tracheostomy", { whitelist }),
      ...whitelistValueDef("specialised_intensive_nurse", { whitelist }),
      ...whitelistValueDef("oxygen_therapy", { whitelist }),
    },
  );

export const careForChildrenAndTeenagersModelDefinition = ({
  whitelist,
}: {
  whitelist: Whitelist;
}) =>
  whitelistActivableInputModelDefinition(
    "patient.care_needs.care_for_children_and_teenagers",
    "care_for_children_and_teenagers",
    {
      whitelist,
      ...whitelistValueDef("description", {
        out_name: "care_for_children_and_teenagers_description",
        whitelist,
      }),
    },
  );

export const careForYouthModelDefinition = ({
  whitelist,
}: {
  whitelist: Whitelist;
}) =>
  whitelistActivableInputModelDefinition(
    "patient.care_needs.care_for_youth",
    "care_for_youth",
    {
      whitelist,
      ...whitelistValueDef("description", {
        out_name: "care_for_youth_description",
        whitelist,
      }),
    },
  );
